import { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNotify, useUpdate } from "react-admin";
import { SectionCard } from "@/components/common/SectionCard";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { AlertType, ResourceName, StoplossClaim } from "@/types";

/**
 * The claim's notes: the one hand edit on the page (#7586 AC4). Explicit
 * Save/Cancel, ⌘/Ctrl+Enter saves, Esc cancels. Round-trips the full record on
 * save so the PUT handler keeps claim_status (#5305). Also shown on the client's
 * Claims Summary (#5273).
 */
export const ClaimNotesCard = ({ claim }: { claim: StoplossClaim }) => {
  const theme = useTheme();
  const [update] = useUpdate();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { canEdit } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(claim.notes ?? "");

  const save = async () => {
    try {
      await update(
        ResourceName.STOPLOSS_CLAIMS,
        { id: claim.id, data: { ...claim, notes: value }, previousData: claim },
        { returnPromise: true }
      );
      notify("Notes updated", { type: AlertType.SUCCESS });
      setEditing(false);
    } catch (e) {
      notifyError(e);
    }
  };
  const cancel = () => {
    setValue(claim.notes ?? "");
    setEditing(false);
  };
  // The card outlives tab switches and refreshes, so the draft starts from the
  // record as it is now, not as it was at mount (review on !5015).
  const startEdit = () => {
    setValue(claim.notes ?? "");
    setEditing(true);
  };

  return (
    <SectionCard
      compact
      title="Notes"
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontStyle: "italic" }}>
            Also on the client’s Claims Summary
          </Typography>
        </Box>
      }
    >
      {editing ? (
        <Box>
          <textarea
            autoFocus
            aria-label="Claim notes"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void save();
              }
            }}
            style={{
              width: "100%",
              minHeight: 120,
              padding: 8,
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              fontFamily: "inherit",
              fontSize: "0.875rem",
              resize: "vertical",
              background: "transparent",
              color: "inherit",
            }}
          />
          <Box sx={{ display: "flex", gap: 1.5, mt: 1, alignItems: "center" }}>
            <Typography variant="caption" color="text.disabled" sx={{ mr: "auto" }}>
              ⌘/Ctrl+Enter to save · Esc to cancel
            </Typography>
            <Typography
              component="button"
              type="button"
              variant="caption"
              color="primary"
              onClick={() => void save()}
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                background: "none",
                border: 0,
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Save
            </Typography>
            <Typography
              component="button"
              type="button"
              variant="caption"
              color="text.secondary"
              onClick={cancel}
              sx={{
                cursor: "pointer",
                background: "none",
                border: 0,
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Cancel
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          role={canEdit ? "button" : undefined}
          tabIndex={canEdit ? 0 : undefined}
          onClick={canEdit ? startEdit : undefined}
          onKeyDown={canEdit ? (e) => (e.key === "Enter" || e.key === " ") && startEdit() : undefined}
          sx={{
            cursor: canEdit ? "pointer" : "default",
            minHeight: 60,
            p: 1,
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "divider",
            ...(canEdit && { "&:hover": { bgcolor: "action.hover", borderColor: "text.secondary" } }),
          }}
        >
          <Typography
            variant="body2"
            color={claim.notes ? "text.primary" : "text.disabled"}
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {claim.notes || (canEdit ? "Click to add notes…" : "No notes.")}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
};
